import { useMemo } from 'react';

import {
	AnswerReveal,
	PointsRow,
	StandardPatternBody,
} from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import { analyzeConnectionQuestion } from '@quizzach/utils';
import {
	OptCols,
	OptList,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';

interface IConnectionQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

interface IConnectionQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

/* Two clean columns on one grid: letters left, numbers right. On the
   answer slide the numbers reorder to sit beside their matching letter. */
const ConnectionColumns = ({
	currentQuestion,
	matchAnswers = false,
}: {
	currentQuestion: IQuestion;
	matchAnswers?: boolean;
}) => {
	const cq = currentQuestion.connection_question;
	if (!cq) return null;
	const letters: Array<[string, string]> = [
		['a', cq.a],
		['b', cq.b],
		['c', cq.c],
		['d', cq.d],
	];
	const numberTexts: Record<string, string> = {
		1: cq.n1,
		2: cq.n2,
		3: cq.n3,
		4: cq.n4,
	};
	const numbers: Array<[string, string]> = matchAnswers
		? [cq.answer_a, cq.answer_b, cq.answer_c, cq.answer_d].map((n) => [
				String(n),
				numberTexts[String(n)] ?? '',
			])
		: [
				['1', cq.n1],
				['2', cq.n2],
				['3', cq.n3],
				['4', cq.n4],
			];
	return (
		<OptCols className={matchAnswers ? 'paired' : undefined}>
			{[letters, numbers].map((column, index) => (
				<OptList key={index}>
					{column.map(([label, html]) => (
						<div className='opt' key={label}>
							<span className='L'>{label}</span>
							<span className='t' dangerouslySetInnerHTML={{ __html: html }} />
						</div>
					))}
				</OptList>
			))}
		</OptCols>
	);
};

const connectionAnswer = (currentQuestion: IQuestion) => {
	const cq = currentQuestion.connection_question;
	if (!cq) return '';
	return `a${cq.answer_a} · b${cq.answer_b} · c${cq.answer_c} · d${cq.answer_d}`;
};

export const ConnectionQuestionDesktop = (props: IConnectionQuestionProps) => {
	const { color, currentQuestion } = props;
	const analysis = useMemo(
		() => analyzeConnectionQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{analysis.pattern || analysis.note || analysis.kicker ? (
				<StandardPatternBody analysis={analysis} color={color} />
			) : (
				<QuestionContainer color={color}>
					<QuestionText>{analysis.questionHtml}</QuestionText>
				</QuestionContainer>
			)}
			<ConnectionColumns currentQuestion={currentQuestion} />
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};

export const ConnectionQuestionAnswerDesktop = (
	props: IConnectionQuestionAnswerProps
) => {
	const { questionContainerColor, currentQuestionNumber, currentQuestion } =
		props;
	const analysis = useMemo(
		() => analyzeConnectionQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{analysis.pattern || analysis.note || analysis.kicker ? (
				<StandardPatternBody
					analysis={analysis}
					color={questionContainerColor}
					dim
				/>
			) : (
				<QuestionContainer color={questionContainerColor}>
					<QuestionText style={{ margin: '20px 0px 0px' }}>
						{analysis.questionHtml}
					</QuestionText>
				</QuestionContainer>
			)}
			<ConnectionColumns currentQuestion={currentQuestion} matchAnswers />
			<AnswerReveal
				revealKey={currentQuestionNumber}
				answerHtml={connectionAnswer(currentQuestion)}
				explanation={currentQuestion.connection_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
